import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { keyframes, css } from 'styled-components';

const ROTATE_MS = 4500;
const REFRESH_MS = 15000;

const MARKET_COLORS = {
  crypto: { primary: '#7c3aed', light: 'rgba(124,58,237,0.1)', glow: 'rgba(124,58,237,0.2)' },
  forex:  { primary: '#0284c7', light: 'rgba(2,132,199,0.1)',  glow: 'rgba(2,132,199,0.2)'  },
  stocks: { primary: '#059669', light: 'rgba(5,150,105,0.1)',  glow: 'rgba(5,150,105,0.2)'  },
};

const MARKET_CONFIG = [
  {
    key: 'crypto', label: 'Crypto',
    symbols: [
      { ticker: 'BINANCE:BTCUSDT', title: 'Bitcoin',   code: 'BTC' },
      { ticker: 'BINANCE:ETHUSDT', title: 'Ethereum',  code: 'ETH' },
      { ticker: 'BINANCE:BNBUSDT', title: 'BNB',       code: 'BNB' },
      { ticker: 'BINANCE:XRPUSDT', title: 'XRP',       code: 'XRP' },
      { ticker: 'BINANCE:SOLUSDT', title: 'Solana',    code: 'SOL' },
      { ticker: 'BINANCE:ADAUSDT', title: 'Cardano',   code: 'ADA' },
      { ticker: 'BINANCE:DOGEUSDT',title: 'Dogecoin',  code: 'DOGE'},
      { ticker: 'BINANCE:TRXUSDT', title: 'TRON',      code: 'TRX' },
      { ticker: 'BINANCE:AVAXUSDT',title: 'Avalanche', code: 'AVAX'},
      { ticker: 'BINANCE:LINKUSDT',title: 'Chainlink', code: 'LINK'},
    ],
  },
  {
    key: 'forex', label: 'Forex',
    symbols: [
      { title: 'Euro / US Dollar',              code: 'EUR/USD', base: 'EUR', quote: 'USD' },
      { title: 'British Pound / US Dollar',     code: 'GBP/USD', base: 'GBP', quote: 'USD' },
      { title: 'US Dollar / Japanese Yen',      code: 'USD/JPY', base: 'USD', quote: 'JPY' },
      { title: 'Australian Dollar / US Dollar', code: 'AUD/USD', base: 'AUD', quote: 'USD' },
      { title: 'US Dollar / Canadian Dollar',   code: 'USD/CAD', base: 'USD', quote: 'CAD' },
      { title: 'US Dollar / Swiss Franc',       code: 'USD/CHF', base: 'USD', quote: 'CHF' },
      { title: 'New Zealand Dollar / US Dollar',code: 'NZD/USD', base: 'NZD', quote: 'USD' },
      { title: 'Euro / British Pound',          code: 'EUR/GBP', base: 'EUR', quote: 'GBP' },
      { title: 'Euro / Japanese Yen',           code: 'EUR/JPY', base: 'EUR', quote: 'JPY' },
      { title: 'British Pound / Japanese Yen',  code: 'GBP/JPY', base: 'GBP', quote: 'JPY' },
    ],
  },
  {
    key: 'stocks', label: 'Stocks',
    symbols: [
      { title: 'Apple',     code: 'AAPL', symbol: 'aapl.us' },
      { title: 'NVIDIA',    code: 'NVDA', symbol: 'nvda.us' },
      { title: 'Microsoft', code: 'MSFT', symbol: 'msft.us' },
      { title: 'Amazon',    code: 'AMZN', symbol: 'amzn.us' },
      { title: 'Alphabet',  code: 'GOOGL',symbol: 'googl.us'},
      { title: 'Meta',      code: 'META', symbol: 'meta.us' },
      { title: 'Tesla',     code: 'TSLA', symbol: 'tsla.us' },
      { title: 'ExxonMobil',code: 'XOM',  symbol: 'xom.us'  },
      { title: 'Chevron',   code: 'CVX',  symbol: 'cvx.us'  },
      { title: 'JPMorgan',  code: 'JPM',  symbol: 'jpm.us'  },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getLastWorkday = (daysBack = 1) => {
  const d = new Date();
  d.setDate(d.getDate() - daysBack);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const calcRate = (usdRates, base, quote) => {
  const b = usdRates[base];
  const q = usdRates[quote];
  return b && q ? q / b : 0;
};

const formatPrice = (quote) => {
  const p = Number(quote?.price ?? 0);
  if (!Number.isFinite(p)) return '--';
  if (quote.marketKey === 'forex') return p.toFixed(p >= 100 ? 3 : 5);
  if (p >= 1000) return p.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (p >= 1)    return p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return p.toFixed(6);
};

const formatChange = (v) => {
  if (!Number.isFinite(v)) return '0.00%';
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
};

// ─── Fetchers ─────────────────────────────────────────────────────────────────

const fetchCryptoQuotes = async (market) => {
  const symbols = market.symbols.map((s) => s.ticker.replace('BINANCE:', ''));
  const enc = encodeURIComponent(JSON.stringify(symbols));

  const [r24, r1h] = await Promise.allSettled([
    fetch(`https://api.binance.com/api/v3/ticker/24hr?symbols=${enc}`),
    fetch(`https://api.binance.com/api/v3/ticker?symbols=${enc}&windowSize=1h`),
  ]);

  if (r24.status !== 'fulfilled' || !r24.value.ok) throw new Error('Binance 24h failed');

  const d24 = await r24.value.json();
  const m24 = new Map(d24.map((r) => [r.symbol, r]));

  let m1h = new Map();
  if (r1h.status === 'fulfilled' && r1h.value.ok) {
    const d1h = await r1h.value.json();
    m1h = new Map(d1h.map((r) => [r.symbol, r]));
  }

  return market.symbols
    .map((s) => {
      const k = s.ticker.replace('BINANCE:', '');
      const row24 = m24.get(k);
      const row1h  = m1h.get(k);
      return {
        marketKey:   market.key,
        marketLabel: market.label,
        code:  s.code,
        title: s.title,
        price:    Number(row24?.lastPrice ?? 0),
        change:   Number(row24?.priceChangePercent ?? 0),
        change1h: row1h ? Number(row1h.priceChangePercent ?? 0) : null,
        source: 'binance',
      };
    })
    .filter((q) => q.price > 0);
};

const fetchForexQuotes = async (market) => {
  const today = getLastWorkday(0);   // most recent workday (today or last Friday)
  const yesterday = getLastWorkday(1);

  const [rToday, rYest] = await Promise.allSettled([
    fetch(`https://api.frankfurter.app/${today}?from=USD`),
    fetch(`https://api.frankfurter.app/${yesterday}?from=USD`),
  ]);

  if (rToday.status !== 'fulfilled' || !rToday.value.ok) {
    // Try /latest as fallback
    const res = await fetch('https://api.frankfurter.app/latest?from=USD');
    if (!res.ok) throw new Error('Frankfurter failed');
    const p = await res.json();
    const rates = { USD: 1, ...(p?.rates || {}) };
    return market.symbols.map((s) => ({
      marketKey: market.key, marketLabel: market.label,
      code: s.code, title: s.title,
      price: calcRate(rates, s.base, s.quote),
      change: null, change1h: null,
      source: 'frankfurter',
    })).filter((q) => q.price > 0);
  }

  const dToday = await rToday.value.json();
  const todayRates = { USD: 1, ...(dToday?.rates || {}) };

  let yesterdayRates = null;
  if (rYest.status === 'fulfilled' && rYest.value.ok) {
    const dYest = await rYest.value.json();
    yesterdayRates = { USD: 1, ...(dYest?.rates || {}) };
  }

  return market.symbols
    .map((s) => {
      const price = calcRate(todayRates, s.base, s.quote);
      let change = null;
      if (yesterdayRates) {
        const prevPrice = calcRate(yesterdayRates, s.base, s.quote);
        if (prevPrice > 0) change = ((price - prevPrice) / prevPrice) * 100;
      }
      return {
        marketKey: market.key, marketLabel: market.label,
        code: s.code, title: s.title,
        price, change, change1h: null,
        source: 'frankfurter',
      };
    })
    .filter((q) => q.price > 0);
};

const fetchStockQuotes = async () => STATIC_FALLBACKS.stocks;

const STATIC_FALLBACKS = {
  crypto: [
    { marketKey:'crypto', marketLabel:'Crypto', code:'BTC', title:'Bitcoin',   price:0, change:null, change1h:null, source:'fallback' },
  ],
  forex: [
    { marketKey:'forex', marketLabel:'Forex', code:'EUR/USD', title:'Euro / US Dollar',              price:1.08,  change:null, change1h:null, source:'fallback' },
    { marketKey:'forex', marketLabel:'Forex', code:'GBP/USD', title:'British Pound / US Dollar',     price:1.27,  change:null, change1h:null, source:'fallback' },
    { marketKey:'forex', marketLabel:'Forex', code:'USD/JPY', title:'US Dollar / Japanese Yen',      price:151.2, change:null, change1h:null, source:'fallback' },
    { marketKey:'forex', marketLabel:'Forex', code:'AUD/USD', title:'Australian Dollar / US Dollar', price:0.66,  change:null, change1h:null, source:'fallback' },
    { marketKey:'forex', marketLabel:'Forex', code:'USD/CAD', title:'US Dollar / Canadian Dollar',   price:1.35,  change:null, change1h:null, source:'fallback' },
  ],
  stocks: [
    { marketKey:'stocks', marketLabel:'Stocks', code:'AAPL',  title:'Apple',     price:214.3, change:null, change1h:null, source:'fallback' },
    { marketKey:'stocks', marketLabel:'Stocks', code:'NVDA',  title:'NVIDIA',    price:887.5, change:null, change1h:null, source:'fallback' },
    { marketKey:'stocks', marketLabel:'Stocks', code:'MSFT',  title:'Microsoft', price:428.6, change:null, change1h:null, source:'fallback' },
    { marketKey:'stocks', marketLabel:'Stocks', code:'AMZN',  title:'Amazon',    price:182.4, change:null, change1h:null, source:'fallback' },
    { marketKey:'stocks', marketLabel:'Stocks', code:'TSLA',  title:'Tesla',     price:177.8, change:null, change1h:null, source:'fallback' },
  ],
};

// ─── Component ────────────────────────────────────────────────────────────────

const LivePriceIndicator = () => {
  const [quotes,      setQuotes]      = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading,   setIsLoading]   = useState(true);
  const [priceFlash,  setPriceFlash]  = useState(null);
  const [progress,    setProgress]    = useState(0);
  const [collapsed,   setCollapsed]   = useState(true);
  const prevPriceRef = useRef(null);
  const rafRef       = useRef(null);

  // ── Fetch data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      const [cr, fr, sr] = await Promise.allSettled([
        fetchCryptoQuotes(MARKET_CONFIG[0]),
        fetchForexQuotes(MARKET_CONFIG[1]),
        fetchStockQuotes(MARKET_CONFIG[2]),
      ]);

      if (!mounted) return;

      const merged = [
        ...(cr.status === 'fulfilled' && cr.value.length ? cr.value : STATIC_FALLBACKS.crypto),
        ...(fr.status === 'fulfilled' && fr.value.length ? fr.value : STATIC_FALLBACKS.forex),
        ...(sr.status === 'fulfilled' && sr.value.length ? sr.value : STATIC_FALLBACKS.stocks),
      ];

      setQuotes(merged);
      setActiveIndex((i) => (i >= merged.length ? 0 : i));
      setIsLoading(false);
    };

    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // ── Rotate ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (quotes.length <= 1) return;
    const t = setInterval(() => setActiveIndex((i) => (i + 1) % quotes.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [quotes.length]);

  // ── Progress bar ───────────────────────────────────────────────────────────
  useEffect(() => {
    setProgress(0);
    const start = performance.now();
    const tick = (now) => {
      const pct = Math.min(((now - start) / ROTATE_MS) * 100, 100);
      setProgress(pct);
      if (pct < 100) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [activeIndex]);

  // ── Active quote ───────────────────────────────────────────────────────────
  const activeQuote = useMemo(() => (quotes.length ? quotes[activeIndex] ?? quotes[0] : null), [quotes, activeIndex]);

  // ── Price flash ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeQuote) return;
    const prev = prevPriceRef.current;
    if (prev !== null && prev !== activeQuote.price) {
      setPriceFlash(activeQuote.price > prev ? 'up' : 'down');
      const t = setTimeout(() => setPriceFlash(null), 900);
      return () => clearTimeout(t);
    }
    prevPriceRef.current = activeQuote.price;
  }, [activeQuote]);

  if (!activeQuote) return null;

  const mc = MARKET_COLORS[activeQuote.marketKey] ?? MARKET_COLORS.crypto;
  const isCrypto = activeQuote.marketKey === 'crypto';

  const change24   = activeQuote.change;
  const change1h   = activeQuote.change1h;
  const has24h     = change24   !== null && Number.isFinite(change24);
  const has1h      = change1h   !== null && Number.isFinite(change1h) && isCrypto;
  const pos24h     = has24h && change24 >= 0;
  const pos1h      = has1h  && change1h >= 0;

  const priceStr   = formatPrice(activeQuote);
  const showCurr   = activeQuote.marketKey !== 'forex';

  const dotCount   = Math.min(quotes.length, 10);
  const activeDot  = activeIndex % dotCount;

  return (
    <Wrap>
      {collapsed ? (
        /* ── Collapsed mini pill ── */
        <MiniPill $color={mc.primary} onClick={() => setCollapsed(false)}>
          <DotWrap style={{ width: 8, height: 8 }}>
            <GreenDot />
            <Ripple1 />
          </DotWrap>
          <MiniCode>{activeQuote.code}</MiniCode>
          <MiniPrice>{showCurr ? '$' : ''}{priceStr}</MiniPrice>
          {has24h && <MiniChange $up={pos24h}>{pos24h ? '▲' : '▼'} {formatChange(change24)}</MiniChange>}
          <MiniExpand title="Expand">↑</MiniExpand>
        </MiniPill>
      ) : (
        /* ── Full card ── */
        <Card>
          <AccentBar $color={mc.primary} />
          <Header $bg={mc.light}>
            <HeaderLeft>
              <DotWrap>
                <GreenDot />
                <Ripple1 />
                <Ripple2 />
              </DotWrap>
              <LiveText>Live Prices</LiveText>
            </HeaderLeft>
            <HeaderRight>
              <RefreshPill>
                <RefreshBlink />{REFRESH_MS / 1000}s
              </RefreshPill>
              <CollapseBtn onClick={() => setCollapsed(true)} title="Minimise">—</CollapseBtn>
            </HeaderRight>
          </Header>

          <AnimatePresence mode="wait">
            <Body
              key={`${activeQuote.marketKey}-${activeQuote.code}-${activeIndex}`}
              initial={{ opacity: 0, x: 18, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -18, scale: 0.96 }}
              transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
            >
              <MetaRow>
                <MBadge $color={mc.primary}>{activeQuote.marketLabel}</MBadge>
                <CodeTag>{activeQuote.code}</CodeTag>
              </MetaRow>
              <AssetTitle>{activeQuote.title}</AssetTitle>
              <PriceRow>
                {showCurr && <CurrSign>$</CurrSign>}
                <Price $flash={priceFlash}>{priceStr}</Price>
                {has24h && <DirectionChip $up={pos24h}>{pos24h ? '▲' : '▼'}</DirectionChip>}
              </PriceRow>
              <ChangesGrid>
                <ChangeCell>
                  <CellLabel>24H Change</CellLabel>
                  {has24h ? <Pill $up={pos24h}><PillArrow>{pos24h ? '▲' : '▼'}</PillArrow>{formatChange(change24)}</Pill> : <PillNA>No data</PillNA>}
                </ChangeCell>
                <CellDivider />
                <ChangeCell>
                  <CellLabel>1H Change</CellLabel>
                  {has1h ? <Pill $up={pos1h}><PillArrow>{pos1h ? '▲' : '▼'}</PillArrow>{formatChange(change1h)}</Pill> : <PillNA>No data</PillNA>}
                </ChangeCell>
              </ChangesGrid>
            </Body>
          </AnimatePresence>

          <Footer>
            <Dots>
              {Array.from({ length: dotCount }).map((_, i) => (
                <DotPip key={i} $active={i === activeDot} $color={mc.primary} />
              ))}
            </Dots>
          </Footer>
          <ProgBar>
            <ProgFill style={{ width: `${progress}%` }} $color={mc.primary} />
          </ProgBar>
        </Card>
      )}
    </Wrap>
  );
};

export default LivePriceIndicator;

// ─── Keyframes ────────────────────────────────────────────────────────────────

const pulseDot = keyframes`
  0%, 100% { transform: scale(1);    opacity: 1;   }
  50%       { transform: scale(0.82); opacity: 0.7; }
`;

const ringOut1 = keyframes`
  0%   { transform: scale(1);   opacity: 0.55; }
  100% { transform: scale(2.8); opacity: 0;    }
`;

const ringOut2 = keyframes`
  0%   { transform: scale(1);   opacity: 0.35; }
  100% { transform: scale(3.8); opacity: 0;    }
`;

const blinkDot = keyframes`
  0%, 100% { opacity: 1;    }
  50%       { opacity: 0.25; }
`;

const flashGreen = keyframes`
  0%   { color: inherit; text-shadow: none; }
  25%  { color: #059669; text-shadow: 0 0 14px rgba(5,150,105,0.5); }
  100% { color: inherit; text-shadow: none; }
`;

const flashRed = keyframes`
  0%   { color: inherit; text-shadow: none; }
  25%  { color: #dc2626; text-shadow: 0 0 14px rgba(220,38,38,0.5); }
  100% { color: inherit; text-shadow: none; }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`
  position: fixed;
  left: 18px;
  bottom: 18px;
  z-index: 9998;

  @media (max-width: 640px) {
    left: 10px;
    bottom: 10px;
  }
`;

const Card = styled.div`
  width: min(282px, calc(100vw - 110px));
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.09);
  box-shadow:
    0 2px 4px rgba(0,0,0,0.04),
    0 8px 24px rgba(0,0,0,0.09),
    0 20px 40px rgba(0,0,0,0.06);
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const AccentBar = styled.div`
  height: 3px;
  background: ${({ $color }) =>
    `linear-gradient(90deg, ${$color}cc 0%, ${$color} 50%, ${$color}99 100%)`};
  background-size: 200% auto;
  animation: ${shimmer} 3s linear infinite;
  transition: background 0.5s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.64rem 0.9rem 0.56rem;
  background: ${({ $bg }) => $bg};
  transition: background 0.5s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.48rem;
`;

const DotWrap = styled.div`
  position: relative;
  width: 10px;
  height: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const GreenDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  position: relative;
  z-index: 2;
  animation: ${pulseDot} 2s ease-in-out infinite;
  box-shadow: 0 0 0 2px rgba(16,185,129,0.25);
`;

const Ripple1 = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.45);
  animation: ${ringOut1} 2.2s ease-out infinite;
`;

const Ripple2 = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.28);
  animation: ${ringOut2} 2.2s ease-out 0.6s infinite;
`;

const LiveText = styled.span`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #374151;
`;

const RefreshPill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.28rem;
  font-size: 0.57rem;
  font-weight: 600;
  color: #6b7280;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 999px;
  padding: 0.2rem 0.5rem;
  letter-spacing: 0.04em;
`;

const RefreshBlink = styled.span`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #10b981;
  animation: ${blinkDot} 1.5s ease-in-out infinite;
`;

const Body = styled(motion.div)`
  padding: 0.82rem 0.9rem 0.76rem;
  display: flex;
  flex-direction: column;
  gap: 0.56rem;
  min-height: 152px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const MBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.22rem 0.56rem;
  border-radius: 999px;
  font-size: 0.57rem;
  font-weight: 800;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: ${({ $color }) => $color};
  background: ${({ $color }) => `${$color}15`};
  border: 1px solid ${({ $color }) => `${$color}30`};
  transition: color 0.4s, background 0.4s, border-color 0.4s;
`;

const CodeTag = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: 0.06em;
`;

const AssetTitle = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.25;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
`;

const CurrSign = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #6b7280;
  line-height: 1;
  align-self: center;
`;

const Price = styled.div`
  font-size: 1.3rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  ${({ $flash }) =>
    $flash === 'up'   && css`animation: ${flashGreen} 0.9s ease forwards;`}
  ${({ $flash }) =>
    $flash === 'down' && css`animation: ${flashRed}   0.9s ease forwards;`}
`;

const DirectionChip = styled.span`
  font-size: 0.7rem;
  font-weight: 800;
  color: ${({ $up }) => ($up ? '#059669' : '#dc2626')};
  background: ${({ $up }) => ($up ? 'rgba(5,150,105,0.1)' : 'rgba(220,38,38,0.1)')};
  border: 1px solid ${({ $up }) => ($up ? 'rgba(5,150,105,0.25)' : 'rgba(220,38,38,0.25)')};
  padding: 0.18rem 0.38rem;
  border-radius: 6px;
  align-self: center;
  line-height: 1;
`;

const ChangesGrid = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0;
  background: #f8fafc;
  border: 1px solid rgba(0, 0, 0, 0.07);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 0.12rem;
`;

const ChangeCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem 0.4rem;
`;

const CellDivider = styled.div`
  width: 1px;
  background: rgba(0, 0, 0, 0.07);
  margin: 0.4rem 0;
`;

const CellLabel = styled.span`
  font-size: 0.54rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #9ca3af;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.28rem 0.52rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ $up }) => ($up ? '#065f46' : '#991b1b')};
  background: ${({ $up }) => ($up ? 'rgba(5,150,105,0.12)' : 'rgba(220,38,38,0.1)')};
  border: 1px solid ${({ $up }) => ($up ? 'rgba(5,150,105,0.28)' : 'rgba(220,38,38,0.25)')};
`;

const PillArrow = styled.span`
  font-size: 0.58rem;
`;

const PillNA = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.28rem 0.52rem;
  border-radius: 8px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #cbd5e1;
  background: rgba(203, 213, 225, 0.15);
  border: 1px solid rgba(203, 213, 225, 0.3);
  letter-spacing: 0.02em;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.42rem 0.9rem 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  background: #fafafa;
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DotPip = styled.span`
  height: 5px;
  width: ${({ $active }) => ($active ? '20px' : '5px')};
  border-radius: 999px;
  background: ${({ $active, $color }) => ($active ? $color : 'rgba(0,0,0,0.14)')};
  transition: width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1),
              background 0.4s ease;
`;

const ProgBar = styled.div`
  height: 2px;
  background: rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const ProgFill = styled.div`
  height: 100%;
  background: ${({ $color }) => `linear-gradient(90deg, ${$color}70, ${$color})`};
  transition: width 0.1s linear;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const CollapseBtn = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.12);
  background: rgba(0,0,0,0.05);
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  padding: 0;
  transition: background 0.15s;

  &:hover {
    background: rgba(0,0,0,0.1);
    color: #374151;
  }
`;

/* Mini collapsed pill */
const MiniPill = styled.button`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.1);
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  cursor: pointer;
  transition: box-shadow 0.18s, transform 0.18s;
  white-space: nowrap;

  &:hover {
    box-shadow: 0 6px 24px rgba(0,0,0,0.18);
    transform: translateY(-1px);
  }
`;

const MiniCode = styled.span`
  font-size: 0.68rem;
  font-weight: 900;
  color: #374151;
  letter-spacing: 0.04em;
`;

const MiniPrice = styled.span`
  font-size: 0.8rem;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
`;

const MiniChange = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  color: ${({ $up }) => ($up ? '#059669' : '#dc2626')};
`;

const MiniExpand = styled.span`
  font-size: 0.7rem;
  color: #9ca3af;
  margin-left: 0.1rem;
`;
